import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import { ReactComponent as ArrowDownIcon } from 'icons/arrowDown.svg';
import { InfoCircle as InfoCircleIcon } from 'icons/InfoCircle';
import type React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCurrentCurrency,
  getRecipient,
  getRecurringOffersMetadata,
} from 'selectors';
import { selectFirstOfferInstallments } from 'selectors/entities/recurringOffers/recurringOffers';
import { createMarkup, useTranslations } from 'utils';
import { date } from 'utils/date/date';

const PlanSummary: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const i18n = useTranslations();
  const currency = useSelector(getCurrentCurrency);
  const recipient = useSelector(getRecipient);
  const installments = useSelector(selectFirstOfferInstallments);
  const metadata = useSelector(getRecurringOffersMetadata);
  const { numberOfInstallments = 0, schedule } = metadata || {};
  const today = date.formatToBrowserLocale(new Date().toString());

  return (
    <div className="mb-6 space-y-2 bg-[#e4f2ff] px-4 py-3">
      <div className="flex items-center">
        <div className="mt-0.5 shrink-0 self-start">
          <InfoCircleIcon
            aria-hidden="true"
            className="block size-[14px] text-[#116EBB]"
          />
        </div>

        <div className="ml-2 flex flex-1 flex-col space-y-3">
          <p
            className="m-0 text-sm text-gray-950"
            dangerouslySetInnerHTML={createMarkup(
              i18n.t('recurringPayment.recipientPlan', {
                recipientName: recipient.name,
                installments: numberOfInstallments,
              }),
            )}
          />

          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-center space-x-2 whitespace-nowrap rounded border border-gray-300 bg-white px-4 py-1.5 text-sm font-normal text-blue-600 shadow-sm"
            onClick={() => setIsOpen(isOpen => !isOpen)}
            data-testid="toggle-calendar"
          >
            <span>
              {isOpen
                ? i18n.t('recurringPayment.hidePlanSummary')
                : i18n.t('recurringPayment.viewPlanSummary')}
            </span>
            <ArrowDownIcon
              className={`size-2 transition-all ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>

          {isOpen && (
            <div className="overflow-auto rounded-t border border-b-0 border-gray-300 bg-white outline outline-white/5">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="p-4">
                    <th className="border-b border-gray-300 p-4 pb-3 text-sm font-semibold uppercase text-gray-950">
                      #
                    </th>
                    <th className="border-b border-gray-300 p-4 pb-3 text-sm font-semibold uppercase text-gray-950">
                      {i18n.t('planSummaryModal.columns.chargeDate')}
                    </th>
                    <th className="border-b border-gray-300 p-4 pb-3 text-sm font-semibold uppercase text-gray-950">
                      {i18n.t('planSummaryModal.columns.installmentAmount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {installments?.map((installment, index) => {
                    const installmentDate = date.formatToBrowserLocale(
                      installment.date,
                    );

                    return (
                      <tr key={index}>
                        <td className="border-b border-gray-300 p-4 text-sm">
                          {index + 1}
                        </td>
                        <td className="border-b border-gray-300 p-4 text-sm">
                          {installmentDate === today ||
                          (index === 0 && schedule === 'pay_now')
                            ? i18n.t('planSummaryModal.today')
                            : installmentDate}
                        </td>
                        <td className="border-b border-gray-300 p-4 text-sm">
                          <FormattedAmount
                            value={installment.amount}
                            currency={currency.code}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PlanSummary };
